import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ConfigurationService } from './configuration.service';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationLockService {
  private lockStatusSubject = new BehaviorSubject<any>(null);
  public lockStatus$ = this.lockStatusSubject.asObservable();

  private pollingInterval: any;
  private currentConfigId: number | null = null;
  private currentUserId: number | null = null;
  private currentProjectId: number | null = null;

  private readonly TOTAL_TIME = 10 * 60; // 10 minutes
  private remainingTime = 0;
  private active = false;
  private tabId: string;
  private readonly WARNING_TIME = 2 * 60; // 2 minutes warning

  private timer$ = new BehaviorSubject<number | null>(null);
  private warningShown = new BehaviorSubject<boolean>(false);
  public remainingTime$ = this.timer$.asObservable();
  public showWarning = this.warningShown.asObservable();

  private intervalId: any;

  private readonly TIMER_STORAGE_KEY = 'config_lock_timer';
  private readonly LOCK_RELEASED_KEY = 'config_lock_released';

  constructor(
    private configurationService: ConfigurationService,
  ) {
    // Generate unique tab ID
    this.tabId = this.generateTabId();
    
    // Register tab lifecycle events
    this.registerTabEvents();
    this.registerUserEvents();
    
    // Sync timer from storage on init
    this.syncTimerFromStorage();
  }

  private generateTabId(): string {
    return `tab_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private registerTabEvents(): void {
    // Handle tab close/refresh - use multiple events for reliability
    // window.addEventListener('beforeunload', () => {
    //   this.handleTabClose();
    // });

    // Handle page hide (more reliable on mobile and some browsers)
    // window.addEventListener('pagehide', (event) => {
    //   if (!(event as PageTransitionEvent).persisted) {
    //     this.handleTabClose();
    //   }
    // });

    // Handle unload as fallback
    window.addEventListener('unload', () => {
      this.handleTabClose();
    });

    // Handle visibility change (tab switch)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.syncTimerFromStorage();
        this.checkIfLockReleased();
      }
    });

    // Listen for storage changes from other tabs
    window.addEventListener('storage', (event) => {
      if (event.key === this.TIMER_STORAGE_KEY) {
        this.syncTimerFromStorage();
      }
      if (event.key === this.LOCK_RELEASED_KEY) {
        // Another tab released the lock - stop editing in this tab too
        this.handleLockReleasedByOtherTab();
      }
    });
  }

  private handleTabClose(): void {
     const currentLockStatus = this.lockStatusSubject.value;
   
     if (this.currentConfigId && this.currentProjectId && this.currentUserId && this.active &&
        currentLockStatus?.isLocked && currentLockStatus?.lockedByUserId === this.currentUserId) {
     // Release lock when tab is closed
       this.configurationService.UnLockConfigurationSync(this.currentProjectId, this.currentConfigId, this.currentUserId);

      this.notifyLockReleased();
      this.clearTimerStorage();
    }
  }

  private notifyLockReleased(): void {
    // Notify other tabs that lock has been released
    if (this.currentConfigId) {
      const releaseData = {
        configId: this.currentConfigId,
        timestamp: Date.now()
      };
      localStorage.setItem(this.LOCK_RELEASED_KEY, JSON.stringify(releaseData));
    }
  }

  private handleLockReleasedByOtherTab(): void {
    const stored = localStorage.getItem(this.LOCK_RELEASED_KEY);
    if (!stored) return;

    try {
      const releaseData = JSON.parse(stored);
      
      // Check if release is for current config
      if (releaseData.configId === this.currentConfigId && this.active) {
        // Stop timer and update lock status
        this.stop();
        this.lockStatusSubject.next({
          isLocked: false,
          lockedByUserId: null,
          lockedByUserName: null
        });
      }
    } catch {
      // Invalid data, ignore
    }
  }

  private checkIfLockReleased(): void {
    const stored = localStorage.getItem(this.LOCK_RELEASED_KEY);
    if (!stored) return;

    try {
      const releaseData = JSON.parse(stored);
      
      // Check if release is for current config and is recent (within last 5 seconds)
      const isRecent = (Date.now() - releaseData.timestamp) < 5000;
      if (releaseData.configId === this.currentConfigId && this.active && isRecent) {
        this.handleLockReleasedByOtherTab();
      }
    } catch {
      // Invalid data, ignore
    }
  }

  private saveTimerToStorage(): void {
    if (this.active && this.currentConfigId) {
      const timerData = {
        configId: this.currentConfigId,
        remainingTime: this.remainingTime,
        lastUpdated: Date.now()
      };
      localStorage.setItem(this.TIMER_STORAGE_KEY, JSON.stringify(timerData));
    }
  }

  private syncTimerFromStorage(): void {
    const stored = localStorage.getItem(this.TIMER_STORAGE_KEY);
    if (!stored) return;

    try {
      const timerData = JSON.parse(stored);
      
      // Only sync if same config
      if (timerData.configId === this.currentConfigId && this.active) {
        // Calculate elapsed time since last update
        const elapsed = Math.floor((Date.now() - timerData.lastUpdated) / 1000);
        const syncedTime = timerData.remainingTime - elapsed;
        
        if (syncedTime > 0) {
          this.remainingTime = syncedTime;
          this.timer$.next(this.remainingTime);
        }
      }
    } catch {
      // Invalid data, ignore
    }
  }

  private clearTimerStorage(): void {
    localStorage.removeItem(this.TIMER_STORAGE_KEY);
  }

  // Get current lock status (for immediate checks)
  getCurrentLockStatus(): any {
    return this.lockStatusSubject.value;
  }

  // Fetch lock status and update subject
  fetchLockStatus(configId: number, projectId: number, userId: number): void {
   // Store the IDs so they're available for tab close handling
    this.currentConfigId = configId;
    this.currentProjectId = projectId;
    this.currentUserId = userId;
   
    this.configurationService.GetLockStatus(projectId, configId, userId, false).subscribe((data: any) => {
      if (data && data.StatusCode == 200) {
        const lockStatus = {
          isLocked: data.Data[0].IsLock,
          lockedByUserId: data.Data[0].UserId,
          lockedByUserName: data.Data[0].UserName,
          ConfigId: data.Data[0].ConfigId,
          ConfigName: data.Data[0].ConfigName
        };
        this.lockStatusSubject.next(lockStatus);
        if (lockStatus.isLocked && lockStatus.lockedByUserId === userId) {
          this.start(configId);
        }
      }
    });
    // Map the API response to the lock status object
    // (so the subscriber gets the lock status directly)
    // You can use map operator from rxjs/operators
    //   map((data: any) => ({
    //     isLocked: data.Data[0].IsLock,
    //     lockedByUserId: data.Data[0].UserId,
    //     lockedByUserName: data.Data[0].UserName
    //   }))
    // );
  }

  // Lock configuration
  // lockConfiguration(configId: number, projectId: number, userId: number): void {
  //   //API call to lock the configuration
  //   this.configurationService.GetLockStatus(projectId, configId, userId, true).subscribe((data: any) => {
  //     console.log(data);
  //     if (data && data.StatusCode == 200) {
  //       if (data.Data[0].ConfigId == configId) {

  //         this.start(configId, this.tabId);
  //       }

  //       // Handle the case when the configuration is already locked
  //       this.lockStatusSubject.next({
  //         isLocked: data.Data[0].IsLock,
  //         lockedByUserId: data.Data[0].UserId,
  //         lockedByUserName: data.Data[0].UserName,
  //         ConfigId: data.Data[0].ConfigId
  //       });

  //     }
  //   });
  // }
  lockConfiguration(configId: number, projectId: number, userId: number): Observable<any> {
    return this.configurationService.GetLockStatus(projectId, configId, userId, true).pipe(
      tap((data: any) => {
        if (data && data.StatusCode == 200 && data.Data[0].ConfigId == configId) {
          this.lockStatusSubject.next({
            isLocked: data.Data[0].IsLock,
            lockedByUserId: data.Data[0].UserId,
            lockedByUserName: data.Data[0].UserName,
            ConfigId: data.Data[0].ConfigId,
            ConfigName: data.Data[0].ConfigName
          });
          this.start(configId);
        }
        // If configId does not match, do NOT update lockStatusSubject
      })
    );
  }

  // Unlock configuration - releases lock for ALL tabs
  unlockConfiguration(configId: number, projectId: number, userId: number): void {
    this.configurationService.UnLockConfiguration(projectId, configId, userId).subscribe((data: any) => {
      if (data && data.StatusCode == 200) {
        // Update the lock status to unlocked
        this.lockStatusSubject.next({
          isLocked: false,
          lockedByUserId: null,
          lockedByUserName: null,
          ConfigId: configId,
          ConfigName: null
        });
          this.notifyLockReleased();
          this.clearTimerStorage();
        this.stop();
      }
    });
  }

  private clearReleaseNotification(): void {
    const stored = localStorage.getItem(this.LOCK_RELEASED_KEY);
    if (stored) {
      try {
        const releaseData = JSON.parse(stored);
        if (releaseData.configId === this.currentConfigId) {
          localStorage.removeItem(this.LOCK_RELEASED_KEY);
        }
      } catch {
        // Ignore
      }
    }
  }

   private handleTimerExpiry(): void {
    // Timer expired - notify all tabs and clear storage
    this.notifyLockReleased();
    this.clearTimerStorage();
    this.stop();
    
    // Emit event for component to handle unlock
    this.timer$.next(0);
  }

  start(configId: number): void {
    this.stop();
    this.active = true;
    this.currentConfigId = configId;
    
    // Check if timer already exists in storage (from another tab)
    const stored = localStorage.getItem(this.TIMER_STORAGE_KEY);
    if (stored) {
      try {
        const timerData = JSON.parse(stored);
        if (timerData.configId === configId) {
          const elapsed = Math.floor((Date.now() - timerData.lastUpdated) / 1000);
          this.remainingTime = Math.max(0, timerData.remainingTime - elapsed);
        } else {
          this.remainingTime = this.TOTAL_TIME;
        }
      } catch {
        this.remainingTime = this.TOTAL_TIME;
      }
    } else {
      this.remainingTime = this.TOTAL_TIME;
    }

    this.timer$.next(this.remainingTime);
    this.saveTimerToStorage();

    this.intervalId = setInterval(() => {
      if (!this.active) {
        return;
      }
      this.remainingTime--;
      this.timer$.next(this.remainingTime);
      this.saveTimerToStorage();

      // Check if 2 minutes remaining - show warning
      if (this.remainingTime === this.WARNING_TIME) {
        this.warningShown.next(true);
      }

      if (this.remainingTime <= 0) {
        this.handleTimerExpiry();
      }
    }, 1000);
  }
  
  stop(): void {
    this.active = false;
    clearInterval(this.intervalId);
    this.timer$.next(null);
    this.warningShown.next(false);
  }

  reset(): void {
    if (!this.active) {
      return;
    }
    this.remainingTime = this.TOTAL_TIME;
    this.timer$.next(this.remainingTime);
    this.saveTimerToStorage();
    this.warningShown.next(false);
  }

  private registerUserEvents(): void {
    ['keydown', 'click',  'touchstart'].forEach(event =>
      window.addEventListener(event, () => this.reset())
    );
  }
 
}
